import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import {
    Heart, MessageCircle, Share2, Bookmark, BookmarkCheck,
    MoreHorizontal, MapPin, Flag, EyeOff, Sparkles, X
} from 'lucide-react';

export const REACTIONS = [
    { code: "useful", label: "Utile", emoji: "👍" },
    { code: "insightful", label: "Pertinent", emoji: "💡" },
    { code: "support", label: "Soutien", emoji: "🤝" },
    { code: "applause", label: "Bravo", emoji: "👏" },
];

const REPORT_REASONS = [
    { code: "misleading", label: "Trompeur" },
    { code: "ads", label: "Publicité excessive" },
    { code: "harassment", label: "Harcèlement" },
    { code: "personal_data", label: "Donnée personnelle" },
    { code: "illegal", label: "Illégal" },
    { code: "impersonation", label: "Usurpation" },
    { code: "off_topic", label: "Hors sujet" },
    { code: "spam", label: "Spam" },
    { code: "other", label: "Autre" },
];

export function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    if (diff < 7 * 86400) return `il y a ${Math.floor(diff / 86400)} j`;
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

export function getInitials(p) {
    const f = p?.first_name?.[0] || ""; const l = p?.last_name?.[0] || "";
    return (f + l).toUpperCase() || "H";
}

export function ReactionPicker({ post, onReact }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <Button variant="ghost" size="sm" data-testid="post-react-btn"
                    onClick={() => setOpen(!open)}>
                <Heart className={`w-4 h-4 mr-1 ${post._userReaction ? "fill-[#FF914D] text-[#FF914D]" : ""}`} />
                {post._userReaction
                    ? REACTIONS.find(r => r.code === post._userReaction)?.label
                    : "Réagir"}
            </Button>
            {open && (
                <div className="absolute bottom-10 left-0 z-30 bg-white border border-slate-200 rounded-xl shadow-lg p-2 flex gap-1">
                    {REACTIONS.map(r => (
                        <button key={r.code}
                                data-testid={`reaction-${r.code}-btn`}
                                onClick={() => { onReact(post, r.code); setOpen(false); }}
                                className="px-2 py-1 rounded-lg hover:bg-purple-50 text-sm flex items-center gap-1"
                                title={r.label}>
                            <span className="text-lg">{r.emoji}</span>
                            <span className="text-xs text-slate-600">{r.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export function ReportDialog({ onClose, onSubmit }) {
    const [reason, setReason] = useState("");
    return (
        <div data-testid="report-dialog"
             className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={onClose}>
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
                <h3 className="font-semibold mb-3 text-slate-900">Signaler cette publication</h3>
                <div className="space-y-1 mb-4 max-h-64 overflow-y-auto">
                    {REPORT_REASONS.map(r => (
                        <label key={r.code} className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-slate-50 rounded text-slate-700">
                            <input type="radio" name="reason" value={r.code}
                                   checked={reason === r.code} onChange={e => setReason(e.target.value)} />
                            {r.label}
                        </label>
                    ))}
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button disabled={!reason} data-testid="report-submit-btn"
                            onClick={() => onSubmit(reason)}
                            className="bg-[#681793] hover:bg-[#5a1380]">
                        Signaler
                    </Button>
                </div>
            </div>
        </div>
    );
}

export function PostCard({ post, onReact, onSave, onShare, onHide, onReport, onOpenComments, dataTestid }) {
    const md = post.metadata || {};
    const counters = {
        reactions: md.reactions_count ?? 0,
        comments: md.comments_count ?? 0,
    };
    const [menuOpen, setMenuOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    return (
        <motion.article
            data-testid={dataTestid || "post-card"}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-5 mb-4 shadow-sm"
        >
            <header className="flex items-start gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D]
                                flex items-center justify-center text-white font-semibold text-sm">
                    {md.first_name ? getInitials({first_name: md.first_name, last_name: md.last_name}) : "H"}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-semibold text-slate-900">
                            {md.first_name} {md.last_name || md.name || "Homly"}
                        </span>
                        {md.title_pro && <span className="text-sm text-slate-500">· {md.title_pro}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-0.5">
                        {md.category_label && (
                            <span className="px-2 py-0.5 rounded-full bg-purple-50 text-[#681793] font-medium">
                                {md.category_label}
                            </span>
                        )}
                        {post.territory_id && (
                            <span className="inline-flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {post.territory_id_name || "Local"}
                            </span>
                        )}
                        <span>· {formatDate(post.created_at)}</span>
                        {md.is_edited && <span className="italic">· modifiée</span>}
                    </div>
                </div>
                <div className="relative">
                    <button onClick={() => setMenuOpen(!menuOpen)}
                            data-testid="post-menu-btn"
                            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                            aria-label="Plus d'options">
                        <MoreHorizontal className="w-4 h-4" />
                    </button>
                    {menuOpen && (
                        <div className="absolute right-0 top-9 z-20 w-48 bg-white border border-slate-200 rounded-lg shadow-lg py-1">
                            <button onClick={() => { setMenuOpen(false); onHide && onHide(post); }}
                                    data-testid="post-hide-btn"
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-700">
                                <EyeOff className="w-4 h-4" /> Masquer
                            </button>
                            <button onClick={() => { setMenuOpen(false); setReportOpen(true); }}
                                    data-testid="post-report-btn"
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-red-600">
                                <Flag className="w-4 h-4" /> Signaler
                            </button>
                        </div>
                    )}
                </div>
            </header>

            {md.title && (
                <h3 className="font-semibold text-slate-900 text-lg mb-2">{md.title}</h3>
            )}
            <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                {md.body_preview || md.body || ""}
            </div>

            {(counters.reactions > 0 || counters.comments > 0) && (
                <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                    {counters.reactions > 0 && <span>{counters.reactions} réaction{counters.reactions > 1 ? "s" : ""}</span>}
                    {counters.comments > 0 && (
                        <button onClick={() => onOpenComments && onOpenComments(post)}
                                className="hover:underline" data-testid="post-comments-count-btn">
                            {counters.comments} commentaire{counters.comments > 1 ? "s" : ""}
                        </button>
                    )}
                </div>
            )}

            {post.reason_label && (
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 italic">
                    <Sparkles className="w-3 h-3 text-[#FF914D]" />
                    {post.reason_label}
                </div>
            )}

            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                <ReactionPicker post={post} onReact={onReact} />
                <Button variant="ghost" size="sm" data-testid="post-comment-btn"
                        onClick={() => onOpenComments && onOpenComments(post)}>
                    <MessageCircle className="w-4 h-4 mr-1" /> Commenter
                </Button>
                <Button variant="ghost" size="sm" data-testid="post-share-btn"
                        onClick={() => onShare && onShare(post)}>
                    <Share2 className="w-4 h-4 mr-1" /> Partager
                </Button>
                <Button variant="ghost" size="sm" data-testid="post-save-btn"
                        onClick={() => onSave && onSave(post)}>
                    {post._saved
                        ? <BookmarkCheck className="w-4 h-4 mr-1 text-[#681793]" />
                        : <Bookmark className="w-4 h-4 mr-1" />}
                    Sauvegarder
                </Button>
            </div>

            {reportOpen && (
                <ReportDialog onClose={() => setReportOpen(false)}
                              onSubmit={(reason) => { onReport && onReport(post, reason); setReportOpen(false); }} />
            )}
        </motion.article>
    );
}
